import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { SubscriptionContext } from '../../contexts/SubscriptionContext';
import subscriptionService from '../../services/subscriptionService';
import Navbar from '../../components/Navbar';
import InternalNavbar from '../../components/InternalNavbar';
import Footer from '../../components/footer';
import { Helmet } from 'react-helmet-async';
import Logo from '../../images/logo.svg';
import './style.css';

const RenewSubscriptionPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { subscription, hasActiveSubscription } = useContext(SubscriptionContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const SUBSCRIPTION_PRICE = 49;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Redirect non-authenticated users to login
  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: '/renew' } });
    }
  }, [user, navigate]);

  const handleRenew = async () => {
    try {
      setLoading(true);
      setError(null);

      const checkoutData = {
        amount: SUBSCRIPTION_PRICE,
        currency: 'EUR',
        success_url: `${window.location.origin}/payment-success`,
        cancel_url: `${window.location.origin}/renew`
      };

      const response = await subscriptionService.createCheckoutSession(checkoutData);

      // Redirect to Stripe checkout
      window.location.href = response.checkout_url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.error?.message || 'Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  if (!hasActiveSubscription) {
    return (
      <div className="renew-page-wrapper">
        <Helmet>
          <title>Renouveler - Anas Arabic Learning</title>
        </Helmet>
        <InternalNavbar />
        <div className="renew-page">
          <div className="renew-arabic-bg" />
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="renew-text-content text-center">
                  <h1>Aucun abonnement actif</h1>
                  <p className="renew-intro">
                    Vous n'avez pas d'abonnement actif à renouveler.
                  </p>
                  <button onClick={() => navigate('/subscribe')} className="proceed-renew-btn">
                    S'abonner maintenant
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="renew-page-wrapper">
      <Helmet>
        <title>Renouveler votre abonnement - Anas Arabic Learning</title>
        <meta name="description" content="Renouvelez votre abonnement Anas Arabic Learning pour continuer votre apprentissage de l'arabe." />
      </Helmet>
      <Navbar Logo={Logo} hclass={'wpo-header-style-1'} />
      <div className="renew-page">
        <div className="renew-arabic-bg" />

        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="renew-text-content">
                <div className="renew-icon">🔄</div>
                <h1>Renouveler votre abonnement</h1>
                <p className="renew-intro">
                  Continuez votre parcours d'apprentissage sans interruption
                </p>

                <div className="current-subscription-box">
                  <h3>Abonnement actuel</h3>
                  <div className="subscription-info">
                    <div className="info-item">
                      <span className="info-label">Type :</span>
                      <span className="info-value">
                        {subscription?.type === 'paid' ? 'Premium' : 'Demo'}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Expire le :</span>
                      <span className="info-value">
                        {formatDate(subscription?.end_date)}
                      </span>
                    </div>
                    {subscription?.days_remaining !== null && (
                      <div className="info-item">
                        <span className="info-label">Jours restants :</span>
                        <span className="info-value highlight">
                          {subscription.days_remaining} jours
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="renew-action-content">
                <div className="renewal-pricing">
                  <h3>Renouvellement</h3>
                  <div className="price-display-renew">
                    <span className="price-amount">${SUBSCRIPTION_PRICE}</span>
                    <span className="price-period">/ 12 mois</span>
                  </div>
                  <div className="renewal-features">
                    <div className="feature-item">
                      <span className="feature-icon">✓</span>
                      <span>12 mois d'accès complet</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">✓</span>
                      <span>Tous les cours et niveaux</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">✓</span>
                      <span>Mises à jour continues</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">✓</span>
                      <span>Support prioritaire</span>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}

                <button
                  className="proceed-renew-btn"
                  onClick={handleRenew}
                  disabled={loading}
                >
                  {loading ? 'Chargement...' : 'Renouveler maintenant'}
                </button>

                <button
                  onClick={() => navigate('/dashboard')}
                  className="btn-secondary-renew"
                >
                  Retour au tableau de bord
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RenewSubscriptionPage;
